import React, { useEffect, useState, useRef } from "react";
import {
  Upload,
  Button,
  Card,
  message,
  Spin,
  Typography,
  Alert,
  Select,
  Row,
  Col,
  Form,
  Progress,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import Layout from "../Layout";

const { Title, Text } = Typography;
const { Option } = Select;
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.21stcenturyav.info";

const BulkUploadPage = () => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({
    progress: 0,
    status: "",
    total: 0,
    processed: 0,
  });
  const accessToken = localStorage.getItem("accessToken");

  // useRef to store the interval ID so it can be cleared on unmount
  const pollingRef = useRef(null);

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const pollTaskProgress = (taskId) => {
    pollingRef.current = setInterval(async () => {
      try {
        const progressResponse = await fetch(
          `${API_BASE_URL}/upload_progress/${taskId}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
        if (!progressResponse.ok) {
          const errorData = await progressResponse.json();
          throw new Error(errorData.detail || "Failed to fetch progress");
        }
        const progressData = await progressResponse.json();
        setUploadProgress(progressData);

        // Stop polling if task is completed or failed
        if (
          progressData.status === "completed" ||
          progressData.status === "failed"
        ) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      } catch (error) {
        console.error("Error polling progress:", error);
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }, 2000);
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning("Please select a CSV file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("file", fileList[0]);

    const uploadUrl = `${API_BASE_URL}/upload_csv`;

    try {
      setUploading(true);
      const response = await fetch(uploadUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Upload failed");
      }

      const data = await response.json();
      setTaskId(data.task_id);
      message.success(data.message || "Bulk upload started.");
      setFileList([]); // Reset file list after upload starts

      // Start polling for progress updates
      pollTaskProgress(data.task_id);
    } catch (error) {
      console.error("Upload error:", error);
      message.error(error.message || "Bulk upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Layout pageTitle="Bulk Upload Products">
      <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
        <Spin spinning={uploading} tip="Processing CSV..." size="large">
          <Card
            title={
              <Title level={3} style={{ margin: 0 }}>
                <UploadOutlined style={{ marginRight: 8 }} />
                Bulk Product Upload
              </Title>
            }
            extra={
              <Button
                type="primary"
                onClick={handleUpload}
                disabled={fileList.length === 0 || uploading}
                loading={uploading}
              >
                {uploading ? "Processing..." : "Start Upload"}
              </Button>
            }
          >
            <Row gutter={16}>
              <Col span={24}>
                <Upload.Dragger
                  accept=".csv"
                  beforeUpload={(file) => {
                    setFileList([file]);
                    return false; // Prevent automatic upload
                  }}
                  fileList={fileList}
                  onRemove={() => setFileList([])}
                  showUploadList={{
                    showRemoveIcon: true,
                    showDownloadIcon: false,
                  }}
                  multiple={false}
                >
                  <div style={{ padding: "40px 20px" }}>
                    <p style={{ fontSize: 16, marginBottom: 8 }}>
                      <UploadOutlined style={{ fontSize: 24 }} />
                    </p>
                    <p style={{ margin: 0 }}>
                      {fileList.length > 0
                        ? fileList[0].name
                        : "Click or drag CSV file to upload"}
                    </p>
                  </div>
                </Upload.Dragger>
              </Col>
            </Row>

            <div style={{ marginTop: 24 }}>
              <Alert
                message="CSV Format Requirements"
                description={
                  <>
                    <Text strong>Required Columns:</Text> Name, SKU, Categories,
                    Brand, GTIN, Price, Stock, FocusKW
                    <br />
                    <Text strong>Optional Columns:</Text> Description, Short
                    Description, Tags
                  </>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <div>
                <Text strong>Example CSV:</Text>
                <pre
                  style={{
                    background: "#f6f8fa",
                    padding: 16,
                    borderRadius: 4,
                    marginTop: 8,
                    overflowX: "auto",
                  }}
                >
                  {`Name,SKU,Categories,Brand,GTIN,Price,Stock,FocusKW,Description,Short Description,Tags
"Gaming Mouse Pad",GMP-77,Gaming,GameStar,555443322110,14.99,50,"gaming accessories","High-quality gaming mouse pad with smooth surface.","Enhance your gaming experience.","gaming, mouse pad, accessories"
"Wireless Headphones",WH-202,Audio,TechBrand,667554433221,99.99,25,"wireless audio","Premium wireless headphones with noise cancellation.","Immerse yourself in music.","wireless, headphones, audio"
`}
                </pre>
              </div>
            </div>
          </Card>

          {/* Display upload progress if a task has started */}
          {taskId && (
            <Card title={<Title level={4}>Upload Progress</Title>} style={{ marginTop: 24 }}>
              <Alert
                message="Bulk Upload Progress"
                description={
                  <>
                    <Text strong>Status:</Text>{" "}
                    {uploadProgress.status
                      ? uploadProgress.status.toUpperCase()
                      : "N/A"}
                    <br />
                    <Text strong>Processed:</Text> {uploadProgress.processed} /{" "}
                    {uploadProgress.total}
                  </>
                }
                type={
                  uploadProgress.status === "failed" ? "error" : "info"
                }
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Progress
                percent={uploadProgress.progress}
                status={
                  uploadProgress.status === "failed"
                    ? "exception"
                    : uploadProgress.status === "completed"
                    ? "success"
                    : "active"
                }
              />
            </Card>
          )}
        </Spin>
      </div>
    </Layout>
  );
};

export default BulkUploadPage;
